import React, { useEffect, useState } from 'react';
import { DonationRecord, PersatuanPaymentSettings } from '../../types';
import { getPersatuanPaymentSettingsDB, DEFAULT_PERSATUAN_PAYMENT_SETTINGS } from '../../services/db';
import { X, Printer, ShieldCheck, CheckCircle2, QrCode } from 'lucide-react';
import { formatCurrencyMYR } from '../../utils/formatters';

interface OfficialDonationReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  donation: DonationRecord | null;
}

export const OfficialDonationReceiptModal: React.FC<OfficialDonationReceiptModalProps> = ({
  isOpen,
  onClose,
  donation
}) => {
  const [persatuanSettings, setPersatuanSettings] = useState<PersatuanPaymentSettings>(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);

  useEffect(() => {
    getPersatuanPaymentSettingsDB().then(setPersatuanSettings).catch(() => {});
  }, []);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !donation) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs overflow-y-auto animate-fade-in print:p-0 print:bg-white">
      <div
        className="relative w-full max-w-xl bg-white border border-slate-200 rounded-3xl shadow-xl overflow-hidden my-6 flex flex-col max-h-[90vh] print:shadow-none print:border-none print:max-h-full print:my-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Action Bar */}
        <div className="px-6 py-4 bg-white border-b border-slate-100 flex items-center justify-between shrink-0 print:hidden">
          <div className="flex items-center gap-2 font-extrabold text-xs text-slate-900">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>Resit Rasmi Sumbangan Infaq</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Cetak Resit</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              aria-label="Tutup"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Official Receipt Body */}
        <div className="p-8 overflow-y-auto space-y-6 flex-1 text-slate-900 custom-scrollbar text-xs bg-white">
          {/* Organization Letterhead */}
          <div className="flex items-start justify-between gap-4 border-b-2 border-slate-900 pb-4">
            <div className="space-y-1">
              <div className="text-[10px] font-black text-asmm-red uppercase tracking-widest">
                PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA KEDAH
              </div>
              <h2 className="text-sm font-black text-slate-900 tracking-tight leading-snug uppercase">
                RESIT RASMI TERIMAAN SUMBANGAN / INFAQ
              </h2>
              <div className="text-[10px] text-slate-500 space-y-0.5">
                <div>No. Pendaftaran JPPM / ROS: <strong>{persatuanSettings.rosRegistrationNo || 'PPM-007-02-28082022'}</strong></div>
                <div>Akaun: {persatuanSettings.bankName || 'Bank Islam Malaysia Berhad'} ({persatuanSettings.accountNo || '02011010087654'})</div>
                <div>Ibu Pejabat: 263, Persiaran Utama 1/9, Kulim Utama, 09000 Kulim, Kedah</div>
              </div>
            </div>

            <div className="text-right space-y-1 shrink-0">
              <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center text-white ml-auto">
                <ShieldCheck className="w-7 h-7 text-emerald-400" />
              </div>
              <span className="inline-block text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded">
                STATUS: LULUS / DIBAYAR
              </span>
            </div>
          </div>

          {/* Receipt Key Metadata Grid */}
          <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-200">
            <div>
              <span className="text-[10px] font-bold text-slate-500 block uppercase">No. Rujukan Invois / Bil:</span>
              <span className="font-mono font-black text-xs text-slate-900">{donation.transactionRef}</span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-500 block uppercase">Tarikh & Masa Bayaran:</span>
              <span className="font-bold text-xs text-slate-900">{donation.createdAt}</span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-500 block uppercase">Diterima Daripada:</span>
              <span className="font-bold text-xs text-slate-900">
                {donation.isAnonymous ? 'Hamba Allah (Penderma Tanpa Nama)' : donation.donorName}
              </span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-500 block uppercase">Kaedah Bayaran:</span>
              <span className="font-bold text-xs text-slate-900">{donation.paymentMethod}</span>
            </div>
          </div>

          {/* Itemized Line Table */}
          <div className="border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-slate-100 text-[10px] font-bold uppercase text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="p-3">Perihal Sumbangan & Tujuan Tabung</th>
                  <th className="p-3 text-right">Jumlah (RM)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                <tr>
                  <td className="p-3">
                    <div className="font-bold text-slate-900">{donation.campaignTitle}</div>
                    <div className="text-[11px] text-slate-500">Niat Infaq: {donation.infaqType || 'Sumbangan Am'}</div>
                    {donation.message && (
                      <div className="text-[11px] text-slate-500 italic mt-0.5">
                        Doa / Catatan: "{donation.message}"
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono font-bold text-slate-900 text-sm">
                    {formatCurrencyMYR(donation.amount)}
                  </td>
                </tr>
              </tbody>
              <tfoot className="bg-slate-50 font-black border-t-2 border-slate-200">
                <tr>
                  <td className="p-3 text-right text-xs uppercase">Jumlah Bersih Diterima:</td>
                  <td className="p-3 text-right text-sm text-asmm-red font-mono font-black">
                    {formatCurrencyMYR(donation.amount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Footer Note & Verification */}
          <div className="pt-4 border-t border-slate-200 flex items-center justify-between gap-4 text-[10px] text-slate-500">
            <div className="space-y-1 max-w-sm leading-relaxed">
              <p>
                * Resit ini dijana secara elektronik oleh Portal Rasmi Persatuan Seni Silat Awang Sulung Merah Muda dan sah tanpa tandatangan fizikal.
              </p>
              <p className="italic">
                "Biduk berlalu kiambang bertaut" — Terima kasih atas sokongan ikhlas anda.
              </p>
            </div>

            <div className="text-center shrink-0 space-y-1">
              <div className="w-14 h-14 bg-slate-100 border border-slate-300 rounded-lg flex items-center justify-center mx-auto text-slate-400">
                <QrCode className="w-10 h-10 text-slate-700" />
              </div>
              <span className="text-[9px] font-mono text-slate-400 block">SAH DI VERIFIKASI</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
